import { ComponentProps, ReactElement, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CancelButton } from 'src/components/Buttons';
import { Content, useContent } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { Button } from 'src/core/ui';
import { AddCustomPaymentTypeDialog } from 'src/dialogs/AddCustomPaymentTypeDialog';
import { useGetCustomPaymentTypesQuery } from 'src/hooks/api/useGetCustomPaymentTypesQuery';
import { useInsertCustomPaymentTypeMutation } from 'src/hooks/api/useInsertCustomPaymentTypeMutation';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  FieldError,
  FieldLabel,
  FieldWrapper,
} from 'src/modals/common/Purchase';
import { ContentId } from 'src/utils/constants/contentId';
import { PAYMENT_METHOD_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  Feature,
  PurchasePaymentMethod,
  PurchasePaymentMethodType,
} from 'src/WebApiController';

/** Prefix for custom payment type keys in the dropdown */
const CUSTOM_TYPE_PREFIX = 'custom_';

/** Sentinel key for the "Add New Type" option */
const ADD_NEW_KEY = '__add_new__';

export type AddPurchasePaymentMethodDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  onSave?: (data: PurchasePaymentMethod) => void | Promise<void>;
  onCancel?: () => void;
};

export function AddPurchasePaymentMethodDialog({
  onSave,
  onCancel,
  ...genericDialogProps
}: AddPurchasePaymentMethodDialogProps): ReactElement | null {
  const requiredText = useContent(ContentId.Required);
  const addNewPaymentTypeLabel = useContent(ContentId.AddNewPaymentType);
  const hasCustomExternalPaymentTypes = useUserHasFeature(
    Feature.CustomExternalPaymentTypes
  );

  const customTypesQuery = useGetCustomPaymentTypesQuery('PO');
  const customPaymentTypes = customTypesQuery.data;
  const insertMutation = useInsertCustomPaymentTypeMutation('PO');

  const [showAddDialog, setShowAddDialog] = useState(false);
  // Track when a custom type is selected so the dropdown displays the custom name
  const [selectedCustomKey, setSelectedCustomKey] = useState<string | null>(
    null
  );

  // Build merged labels: standard types + custom types + "Add New"
  const mergedLabels = useMemo(() => {
    const labels: Record<string, string> = {
      ...(PAYMENT_METHOD_TYPE_TO_CID as Record<string, string>),
    };
    if (hasCustomExternalPaymentTypes && customPaymentTypes) {
      for (const ct of customPaymentTypes) {
        labels[`${CUSTOM_TYPE_PREFIX}${ct.id}`] = ct.name;
      }
      labels[ADD_NEW_KEY] = addNewPaymentTypeLabel;
    }
    return labels;
  }, [
    hasCustomExternalPaymentTypes,
    customPaymentTypes,
    addNewPaymentTypeLabel,
  ]);

  const {
    control,
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<PurchasePaymentMethod>({
    defaultValues: {
      id: -1,
    },
  });

  const handleTypeChange = (
    value: string,
    fieldOnChange: (value: string) => void
  ) => {
    // Intercept "Add New Type" selection
    if (value === ADD_NEW_KEY) {
      setShowAddDialog(true);
      return;
    }

    // Check if a custom type was selected
    if (value.startsWith(CUSTOM_TYPE_PREFIX)) {
      // Set the underlying type to Other (for the backend)
      fieldOnChange(PurchasePaymentMethodType.Other);
      // Store the selected custom key so the dropdown displays the custom name
      setSelectedCustomKey(value);
      return;
    }

    // Standard type selected — clear custom key
    setSelectedCustomKey(null);
    fieldOnChange(value);
  };

  const handleAddCustomTypeSave = async (name: string) => {
    try {
      await insertMutation.mutateAsync({ name });
      setShowAddDialog(false);
      // Set the type to Other and auto-select the newly created custom type
      setValue('type', PurchasePaymentMethodType.Other);
      const newCustomKey = `${CUSTOM_TYPE_PREFIX}new`;
      setSelectedCustomKey(newCustomKey);
    } catch {
      // Mutation error is handled by React Query
    }
  };

  return (
    <>
      <GenericDialog
        size="md"
        header={<Content id={ContentId.AddPurchasePaymentMethod} />}
        footer={
          <>
            <CancelButton disabled={isSubmitting} onClick={onCancel} />
            <Button
              variant={'regular'}
              disabled={isSubmitting}
              onClick={onSave ? handleSubmit(onSave) : undefined}
            >
              <Content id={ContentId.Save} />
            </Button>
          </>
        }
        onClosed={() => {
          reset();
        }}
        {...genericDialogProps}
        onCancel={onCancel}
      >
        <FieldWrapper>
          <FieldLabel>
            <Content id={ContentId.Type} />
          </FieldLabel>
          <Controller
            control={control}
            name="type"
            rules={{ required: requiredText }}
            render={({ field: { value, onChange, ...field } }) => (
              <PosSelect
                placeholderText={ContentId.ChoosePaymentMethodTypePlaceholder}
                valueOptionsContent={mergedLabels}
                value={selectedCustomKey ?? value}
                disabled={isSubmitting}
                hasErrors={Boolean(errors.type)}
                onChange={(v) => handleTypeChange(v, onChange)}
                {...field}
              />
            )}
          />
          {errors.type && <FieldError>{errors.type?.message}</FieldError>}
        </FieldWrapper>
        <FieldWrapper>
          <PosFormField
            errors={errors.name?.message}
            label={<Content id={ContentId.Name} />}
          >
            <PosTextField
              rootProps={{
                disabled: isSubmitting,
                state: getTextFieldState(errors.name),
              }}
              disabled={isSubmitting}
              {...register('name', {
                required: { value: true, message: requiredText },
              })}
            />
          </PosFormField>
        </FieldWrapper>
        <FieldWrapper>
          <PosFormField
            errors={errors.display?.message}
            label={<Content id={ContentId.PaymentAccount} />}
          >
            <PosTextField
              rootProps={{
                disabled: isSubmitting,
                state: getTextFieldState(errors.display),
              }}
              spellCheck={false}
              disabled={isSubmitting}
              {...register('display', {
                required: { value: true, message: requiredText },
              })}
            />
          </PosFormField>
        </FieldWrapper>
      </GenericDialog>

      <AddCustomPaymentTypeDialog
        isOpen={showAddDialog}
        onSave={handleAddCustomTypeSave}
        onCancel={() => setShowAddDialog(false)}
      />
    </>
  );
}
